import React, { useEffect, useRef, useState } from "react";
import { Autocomplete, Box, Chip, CircularProgress, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";

/**
 * Async picker for the Session Recorder.
 *
 * Two modes:
 *   - default: type Nchars to search (min 3), no request on focus.
 *   - loadOnOpen: fetch an initial list on focus with an empty query,
 *     then narrow with as few as 1 typed character. Includes an optional
 *     "group" segmented control (e.g. My ABA Students / All).
 */
export default function AsyncPicker(props: {
  label: string;
  value: any;
  onChange: (v: any) => void;
  fetch: (params: any) => any;
  disabled?: boolean;
  required?: boolean;
  minChars?: number;
  loadOnOpen?: boolean;
  placeholder?: string;
  loadingText?: string;
  emptyText?: string;
  extraQueryParams?: Record<string, any>;
  renderOption?: (option: any) => React.ReactNode;
  groupFilter?: {
    value: string; onChange: (v: string) => void;
    options: Array<{ value: string; label: string }>;
  };
}) {
  const {
    label, value, onChange, fetch, disabled, required,
    minChars: rawMinChars, loadOnOpen, placeholder, loadingText, emptyText,
    extraQueryParams, renderOption, groupFilter,
  } = props;
  const minChars = rawMinChars ?? (loadOnOpen ? 0 : 3);
  const [input, setInput] = useState("");
  const [options, setOptions] = useState<any[]>(value ? [value] : []);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const debounced = useDebounce(input, 400);
  const reqIdRef = useRef(0);
  const extraKey = JSON.stringify(extraQueryParams || {});
  const groupValue = groupFilter?.value ?? "";

  useEffect(() => {
    const q = debounced || "";
    if (!loadOnOpen && q.length < minChars) return;
    // In loadOnOpen mode don't run before the field is opened at least once.
    if (loadOnOpen && !hasFetched && !open) return;
    setLoading(true);
    const rid = ++reqIdRef.current;
    Promise.resolve(fetch({ q, ...(extraQueryParams || {}) }))
      .then((r: any) => {
        if (rid !== reqIdRef.current) return;
        const data = Array.isArray(r?.data?.data) ? r.data.data : (Array.isArray(r?.data) ? r.data : []);
        setOptions(data);
        setHasFetched(true);
      })
      .catch(() => {})
      .finally(() => { if (rid === reqIdRef.current) setLoading(false); });
  }, [debounced, minChars, extraKey, loadOnOpen, open, groupValue]);

  const noOptionsText = loading
    ? (loadingText ?? "Searching…")
    : (input.length < minChars && !loadOnOpen ? `Type ${minChars}+ characters`
        : (emptyText ?? "No matches"));

  return (
    <Autocomplete
      value={value}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      onChange={(_, v) => onChange(v)}
      onInputChange={(_, v) => setInput(v)}
      options={options}
      getOptionLabel={(o: any) => (o?.label ?? "")}
      isOptionEqualToValue={(a: any, b: any) => a?.id === b?.id}
      disabled={disabled}
      size="small"
      loading={loading}
      loadingText={loadingText ?? "Loading…"}
      noOptionsText={noOptionsText}
      renderOption={renderOption
        ? (li, option) => <Box component="li" {...li}>{renderOption(option)}</Box>
        : undefined}
      renderInput={(params) => (
        <>
          {groupFilter && open && (
            <Box sx={{ mb: 0.5 }}>
              <Select fullWidth size="small" value={groupFilter.value}
                onChange={(e) => groupFilter.onChange(e.target.value)}>
                {groupFilter.options.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </Box>
          )}
          <TextField {...params} label={label} required={required} placeholder={placeholder}
            InputProps={{ ...params.InputProps, endAdornment: (<>{loading ? <CircularProgress size={14} /> : null}{params.InputProps.endAdornment}</>) }} />
        </>
      )}
    />
  );
}
